/**
 * Query Performance Monitor
 * 
 * Tracks query performance metrics including:
 * - Query execution time
 * - Slow query detection
 * - Cache hit/miss rates
 * - Query frequency analysis
 */

interface QueryMetric {
  queryKey: string;
  startTime: number;
  endTime?: number;
  duration?: number;
  status: 'pending' | 'success' | 'error';
  fromCache: boolean;
  errorMessage?: string;
}

interface QueryStats {
  totalQueries: number;
  cacheHits: number;
  cacheMisses: number;
  averageDuration: number;
  slowQueries: QueryMetric[];
  queryFrequency: Record<string, number>;
}

/** Threshold for slow query detection (in ms) */
const SLOW_QUERY_THRESHOLD = 500;

/** Store for query metrics */
const queryMetrics: QueryMetric[] = [];
const MAX_METRICS_STORED = 500;

/** Track in-flight queries */
const pendingQueries = new Map<string, number>();

/**
 * Start tracking a query
 */
export function startQueryTracking(queryKey: unknown[]): void {
  const key = JSON.stringify(queryKey);
  pendingQueries.set(key, performance.now());
}

/**
 * End tracking a query and record metrics
 */
export function endQueryTracking(
  queryKey: unknown[],
  status: 'success' | 'error',
  fromCache: boolean = false,
  errorMessage?: string
): void {
  const key = JSON.stringify(queryKey);
  const startTime = pendingQueries.get(key);
  
  if (startTime === undefined) return;
  
  const endTime = performance.now();
  const duration = endTime - startTime;
  
  const metric: QueryMetric = {
    queryKey: key,
    startTime,
    endTime,
    duration,
    status,
    fromCache,
    errorMessage,
  };
  
  queryMetrics.push(metric);
  pendingQueries.delete(key);
  
  /** Log slow queries immediately */
  if (duration > SLOW_QUERY_THRESHOLD) {
    console.warn(`🐢 Slow Query Detected (${duration.toFixed(0)}ms):`, queryKey);
  }
  
  /** Keep metrics array bounded */
  if (queryMetrics.length > MAX_METRICS_STORED) {
    queryMetrics.shift();
  }
}

/**
 * Get aggregated query statistics
 */
export function getQueryStats(): QueryStats {
  const completed = queryMetrics.filter(m => m.status !== 'pending');
  const cacheHits = completed.filter(m => m.fromCache).length;
  const durations = completed.filter(m => m.duration).map(m => m.duration!);
  const avgDuration = durations.length > 0 
    ? durations.reduce((a, b) => a + b, 0) / durations.length 
    : 0;
  
  /** Calculate query frequency */
  const frequency: Record<string, number> = {};
  completed.forEach(m => {
    const simpleKey = getSimpleQueryKey(m.queryKey);
    frequency[simpleKey] = (frequency[simpleKey] || 0) + 1;
  });
  
  /** Find slow queries */
  const slowQueries = completed.filter(
    m => m.duration && m.duration > SLOW_QUERY_THRESHOLD
  );
  
  return {
    totalQueries: completed.length,
    cacheHits,
    cacheMisses: completed.length - cacheHits,
    averageDuration: avgDuration,
    slowQueries,
    queryFrequency: frequency,
  };
}

/**
 * Get a simplified query key for frequency analysis
 */
function getSimpleQueryKey(fullKey: string): string {
  try {
    const parsed = JSON.parse(fullKey);
    if (Array.isArray(parsed) && parsed.length > 0) {
      return typeof parsed[0] === 'string' ? parsed[0] : fullKey;
    }
    return fullKey;
  } catch {
    return fullKey;
  }
}

/**
 * Log query performance summary to console
 */
export function logQueryPerformanceSummary(): void {
  const stats = getQueryStats();
  const cacheHitRate = stats.totalQueries > 0 
    ? ((stats.cacheHits / stats.totalQueries) * 100).toFixed(1)
    : '0';
  
  console.group('📊 Query Performance Summary');
  console.log(`Total Queries: ${stats.totalQueries}`);
  console.log(`Cache Hit Rate: ${cacheHitRate}%`);
  console.log(`Average Duration: ${stats.averageDuration.toFixed(0)}ms`);
  console.log(`Slow Queries (>${SLOW_QUERY_THRESHOLD}ms): ${stats.slowQueries.length}`);
  
  if (stats.slowQueries.length > 0) {
    console.group('🐢 Slow Queries');
    stats.slowQueries.forEach(q => {
      console.log(`${q.duration?.toFixed(0)}ms - ${q.queryKey}`);
    });
    console.groupEnd();
  }
  
  console.group('📈 Query Frequency');
  Object.entries(stats.queryFrequency)
    .sort(([, a], [, b]) => b - a)
    .slice(0, 10)
    .forEach(([key, count]) => {
      console.log(`${count}x - ${key}`);
    });
  console.groupEnd();
  
  console.groupEnd();
}

/**
 * Clear all stored metrics
 */
export function clearQueryMetrics(): void {
  queryMetrics.length = 0;
  pendingQueries.clear();
}

/**
 * Export metrics for external analysis
 */
export function exportQueryMetrics(): QueryMetric[] {
  return [...queryMetrics];
}

declare global {
  interface Window {
    __queryMonitor?: {
      getStats: typeof getQueryStats;
      logSummary: typeof logQueryPerformanceSummary;
      exportMetrics: typeof exportQueryMetrics;
      clearMetrics: typeof clearQueryMetrics;
    };
  }
}

/** Make available globally in development for debugging */
if (typeof window !== 'undefined' && import.meta.env.DEV) {
  window.__queryMonitor = {
    getStats: getQueryStats,
    logSummary: logQueryPerformanceSummary,
    exportMetrics: exportQueryMetrics,
    clearMetrics: clearQueryMetrics,
  };
  console.log('💡 Query monitor available: window.__queryMonitor.logSummary()');
}
